"use client";

import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type AlertType = "success" | "error" | "warning" | "info";

interface Alert {
  id: string;
  type: AlertType;
  message: string;
  duration?: number;
}

interface AlertContextType {
  alerts: Alert[];
  showAlert: (type: AlertType, message: string, duration?: number) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  removeAlert: (id: string) => void;
}

const AlertContext = createContext<AlertContextType>({
  alerts: [],
  showAlert: () => {},
  showSuccess: () => {},
  showError: () => {},
  showWarning: () => {},
  showInfo: () => {},
  removeAlert: () => {},
});

export const useAlert = () => useContext(AlertContext);

const TYPE_STYLES: Record<AlertType, string> = {
  success: "bg-emerald-50 border-emerald-200 text-emerald-800",
  error: "bg-red-50 border-red-200 text-red-800",
  warning: "bg-amber-50 border-amber-200 text-amber-800",
  info: "bg-sky-50 border-sky-200 text-sky-800",
};

const TYPE_LABELS: Record<AlertType, string> = {
  success: "Success",
  error: "Error",
  warning: "Warning",
  info: "Info",
};

export function AlertProvider({ children }: { children: React.ReactNode }) {
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const removeAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const showAlert = useCallback(
    (type: AlertType, message: string, duration = 5000) => {
      const id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      setAlerts((prev) => [...prev, { id, type, message, duration }]);
      if (duration > 0) {
        setTimeout(() => removeAlert(id), duration);
      }
    },
    [removeAlert],
  );

  const showSuccess = useCallback(
    (message: string) => showAlert("success", message),
    [showAlert],
  );
  const showError = useCallback(
    (message: string) => showAlert("error", message),
    [showAlert],
  );
  const showWarning = useCallback(
    (message: string) => showAlert("warning", message),
    [showAlert],
  );
  const showInfo = useCallback(
    (message: string) => showAlert("info", message),
    [showAlert],
  );

  const value = useMemo(
    () => ({
      alerts,
      showAlert,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      removeAlert,
    }),
    [
      alerts,
      showAlert,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      removeAlert,
    ],
  );

  return (
    <AlertContext.Provider value={value}>
      {children}
      {/* Toast renderer — fixed top-right, dismissible, auto-stacked */}
      <div
        aria-live="polite"
        className="pointer-events-none fixed top-4 right-4 z-[200] flex w-full max-w-sm flex-col gap-2"
      >
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className={`pointer-events-auto rounded-lg border px-4 py-3 shadow-md ${TYPE_STYLES[alert.type]}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 text-sm">
                <p className="font-semibold">{TYPE_LABELS[alert.type]}</p>
                <p className="mt-0.5 break-words">{alert.message}</p>
              </div>
              <button
                type="button"
                onClick={() => removeAlert(alert.id)}
                className="text-current opacity-60 hover:opacity-100"
                aria-label="Dismiss"
              >
                <span aria-hidden>×</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </AlertContext.Provider>
  );
}
